import React, { useRef, useState, useCallback, useEffect } from 'react';
import debounce from 'lodash.debounce';
import PulseLoader from '../Loaders/PulseLoader';

const SearchComponent = ({ handleSearch, clearEnabled, handleCleared, className }) => {
    const [searchText, setSearchText] = useState('');
    const [isFetching, setIsFetching] = useState(false);
    const inputRef = useRef();
    // Stable refs so the debounced function never goes stale
    const handleSearchRef = useRef(handleSearch);
    const requestIdRef = useRef(0);

    useEffect(() => {
        handleSearchRef.current = handleSearch;
    }, [handleSearch]);

    const performSearch = useCallback(async (value) => {
        const currentRequestId = ++requestIdRef.current;
        setIsFetching(true);
        try {
            await handleSearchRef.current(value);
        } finally {
            if (requestIdRef.current === currentRequestId) {
                setIsFetching(false);
            }
        }
    }, []);

    // Single stable debounced function — never recreated
    const debouncedSearch = useCallback(
        debounce((value) => {
            performSearch(value);
        }, 300),
        [performSearch]
    );

    // Cleanup on unmount
    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch]);

    const onTextChange = (event) => {
        event.preventDefault();
        var { value } = event.target;
        setSearchText(value);
        // Cancel any pending debounced call so only the latest keystroke fires
        debouncedSearch.cancel();
        debouncedSearch(value);
    };

    const onHandleCleared = async () => {
        debouncedSearch.cancel();
        requestIdRef.current++;
        inputRef.current.value = '';
        setSearchText('');
        setIsFetching(true);
        try {
            await handleCleared();
        } finally {
            setIsFetching(false);
        }
    };

    return (
        <div className={`mrnda-input-text--search${className ? ` ${className}` : ''}`}>
            
            <span className={`mrnda-icon-btn`}>
                {
                    isFetching 
                    ? <PulseLoader backgroundColor='var(--primary-text-color)' size='16px'/>
                    : <i className={ 'fas fa-search'} />
                }
            </span>

            <input
                ref={inputRef}
                type="text"
                className="mrnda-input-text"
                placeholder="Search..."
                onChange={onTextChange}
            />

            {clearEnabled === true && searchText !== '' && (
                <span className="mrnda-icon-btn mrnda-icon-btn-close" onClick={onHandleCleared}>
                    <i className="fas fa-times" />
                </span>
            )}
        </div>
    );
};

export default SearchComponent;
